import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { categories } from '@/mocks/comics';

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [categoryOpen, setCategoryOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
    setCategoryOpen(false);
  }, [location.pathname]);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-background-50/95 backdrop-blur-md shadow-sm'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 group">
            <div className="w-9 h-9 rounded-lg bg-primary-500 flex items-center justify-center group-hover:bg-primary-600 transition-colors">
              <i className="ri-book-read-line text-background-50 text-lg"></i>
            </div>
            <span className="font-heading text-xl font-bold text-foreground-900 group-hover:text-primary-600 transition-colors">
              歪歪漫画
            </span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-8">
            <Link
              to="/"
              className={`text-sm font-medium transition-colors whitespace-nowrap ${
                location.pathname === '/'
                  ? 'text-primary-500'
                  : 'text-foreground-700 hover:text-primary-500'
              }`}
            >
              首页
            </Link>

            {/* Category Dropdown */}
            <div
              className="relative"
              onMouseEnter={() => setCategoryOpen(true)}
              onMouseLeave={() => setCategoryOpen(false)}
            >
              <button
                className={`text-sm font-medium transition-colors whitespace-nowrap flex items-center gap-1 ${
                  location.pathname.startsWith('/category')
                    ? 'text-primary-500'
                    : 'text-foreground-700 hover:text-primary-500'
                }`}
              >
                分类
                <i
                  className={`ri-arrow-down-s-line text-sm transition-transform ${
                    categoryOpen ? 'rotate-180' : ''
                  }`}
                ></i>
              </button>
              {categoryOpen && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 pt-2 animate-fade-in">
                  <div className="bg-background-50 rounded-lg p-3 min-w-[480px] shadow-lg border border-background-200/70">
                    <div className="grid grid-cols-4 gap-1">
                      {categories.map((cat) => (
                        <Link
                          key={cat.slug}
                          to={`/category/${cat.slug}`}
                          className="flex items-center gap-2 px-3 py-2 rounded-md hover:bg-background-100 transition-colors group"
                        >
                          <div className="w-7 h-7 rounded-md bg-primary-100 flex items-center justify-center group-hover:bg-primary-200 transition-colors">
                            <i className={`${cat.icon} text-primary-600 text-xs`}></i>
                          </div>
                          <div>
                            <span className="text-sm font-medium text-foreground-800">{cat.name}</span>
                            <span className="text-xs text-foreground-500 ml-1">{cat.count}</span>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <Link
              to="/about"
              className={`text-sm font-medium transition-colors whitespace-nowrap ${
                location.pathname === '/about'
                  ? 'text-primary-500'
                  : 'text-foreground-700 hover:text-primary-500'
              }`}
            >
              关于我们
            </Link>
          </div>

          {/* Search & Mobile Toggle */}
          <div className="flex items-center gap-3">
            <div className="hidden md:flex items-center bg-background-100 rounded-full px-4 py-2 gap-2">
              <i className="ri-search-line text-foreground-400 text-sm"></i>
              <input
                type="text"
                placeholder="搜索漫画..."
                className="bg-transparent text-sm text-foreground-800 placeholder-foreground-400 outline-none w-40 focus:w-56 transition-all"
              />
            </div>

            <button
              className="md:hidden w-9 h-9 rounded-lg flex items-center justify-center hover:bg-background-100 transition-colors"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Toggle menu"
            >
              <i className={`text-xl text-foreground-700 ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/70 animate-fade-in">
          <div className="px-4 py-3">
            <div className="flex items-center bg-background-100 rounded-full px-4 py-2.5 gap-2 mb-3">
              <i className="ri-search-line text-foreground-400 text-sm"></i>
              <input
                type="text"
                placeholder="搜索漫画..."
                className="bg-transparent text-sm text-foreground-800 placeholder-foreground-400 outline-none flex-1"
              />
            </div>
            <Link
              to="/"
              className="block px-3 py-2.5 text-sm font-medium text-foreground-700 hover:text-primary-500 rounded-md hover:bg-background-100 transition-colors"
            >
              首页
            </Link>
            <div className="px-3 py-2 text-xs font-medium text-foreground-400 uppercase tracking-wide">
              漫画分类
            </div>
            <div className="grid grid-cols-2 gap-1">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="flex items-center gap-2 px-3 py-2 rounded-md hover:bg-background-100 transition-colors"
                >
                  <i className={`${cat.icon} text-primary-500 text-sm`}></i>
                  <span className="text-sm text-foreground-700">{cat.name}</span>
                </Link>
              ))}
            </div>
            <Link
              to="/about"
              className="block px-3 py-2.5 text-sm font-medium text-foreground-700 hover:text-primary-500 rounded-md hover:bg-background-100 transition-colors mt-1"
            >
              关于我们
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
}